/**
 * usePretext � text measurement without DOM reflow
 *
 * Wraps @chenglou/pretext so components can know their rendered height
 * before the browser does a layout pass. Eliminates the measure-then-reflow
 * hot path on streaming text and dynamically populated lists.
 *
 * Usage:
 *   const { height, lineCount } = usePretext(text, containerWidth, {
 *     font: '14px ui-sans-serif, system-ui, sans-serif',
 *     lineHeight: 22,
 *   })
 *
 * Returns null height/lineCount when width is not yet known (SSR, first paint).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { prepare, layout } from '@chenglou/pretext'

export interface PretextOptions {
  /** CSS font string: size + family. Default matches Tailwind's prose body. */
  font?: string
  /** Line height in px. Default 22. */
  lineHeight?: number
  /** Debounce window for text updates in ms. Default 60 (one render frame). */
  debounceMs?: number
}

export interface PretextResult {
  height: number | null
  lineCount: number | null
  /** True while a measurement is pending (debouncing) */
  isPending: boolean
}

const DEFAULT_FONT = '14px ui-sans-serif, system-ui, -apple-system, sans-serif'
const DEFAULT_LINE_HEIGHT = 22

export function usePretext(
  text: string,
  containerWidth: number | null,
  options: PretextOptions = {},
): PretextResult {
  const {
    font = DEFAULT_FONT,
    lineHeight = DEFAULT_LINE_HEIGHT,
    debounceMs = 60,
  } = options

  const [result, setResult] = useState<{ height: number | null; lineCount: number | null }>({
    height: null,
    lineCount: null,
  })
  const [isPending, setIsPending] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Memoize the prepare() call � re-run only when text or font changes.
  // prepare() is the expensive ~19ms step; layout() is 0.09ms.
  const prepared = useMemo(() => {
    if (!text) return null
    try {
      return prepare(text, font)
    } catch {
      return null
    }
  }, [text, font])

  useEffect(() => {
    if (!prepared || containerWidth === null || containerWidth <= 0) {
      setResult({ height: null, lineCount: null })
      return
    }

    setIsPending(true)

    if (timerRef.current !== null) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(() => {
      try {
        const { height, lineCount } = layout(prepared, containerWidth, lineHeight)
        setResult({ height, lineCount })
      } catch {
        setResult({ height: null, lineCount: null })
      } finally {
        setIsPending(false)
      }
    }, debounceMs)

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [prepared, containerWidth, lineHeight, debounceMs])

  return { ...result, isPending }
}

/**
 * useContainerWidth � measures an element's inner width reactively.
 * Returns null until the ref is attached and observed.
 */
export function useContainerWidth<T extends HTMLElement>(): [
  React.RefCallback<T>,
  number | null,
] {
  const [width, setWidth] = useState<number | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref: React.RefCallback<T> = (el) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (el) {
      setWidth(el.clientWidth)
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) setWidth(Math.floor(entry.contentRect.width))
      })
      ro.observe(el)
      observerRef.current = ro
    }
  }

  return [ref, width]
}
